import { fromCallback } from "xstate";
import type { EventObject } from "xstate";

import { APPEARANCE_COOKIE_NAME, resolveTheme, SYSTEM_THEME_QUERY } from "@/lib/state/appearance";
import type { Appearance, SystemPreferenceChangedEvent } from "@/lib/state/appearance";

const APPEARANCE_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export const observeSystemPreference = fromCallback<EventObject>(({ sendBack }) => {
  let disposed = false;
  const mediaQuery = window.matchMedia(SYSTEM_THEME_QUERY);
  const sendObservation = (event: SystemPreferenceChangedEvent): void => {
    sendBack(event);
  };

  const handleSystemPreferenceChange = (event: Readonly<MediaQueryListEvent>): void => {
    if (disposed) {
      return;
    }

    sendObservation({
      systemPrefersDark: event.matches,
      type: "SYSTEM_PREFERENCE_CHANGED",
    });
  };

  mediaQuery.addEventListener("change", handleSystemPreferenceChange);
  sendObservation({
    systemPrefersDark: mediaQuery.matches,
    type: "SYSTEM_PREFERENCE_CHANGED",
  });

  return () => {
    disposed = true;
    mediaQuery.removeEventListener("change", handleSystemPreferenceChange);
  };
});

export const projectBrowserAppearance = (
  appearance: Appearance,
  systemPrefersDark: boolean,
): void => {
  const resolvedTheme = resolveTheme(appearance, systemPrefersDark);
  const isDark = resolvedTheme === "dark";

  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = resolvedTheme;
};

export const persistBrowserAppearance = (appearance: Appearance): void => {
  // oxlint-disable-next-line unicorn/no-document-cookie -- this synchronous write keeps the next server render aligned without requiring Cookie Store API support.
  document.cookie = `${APPEARANCE_COOKIE_NAME}=${appearance}; Path=/; Max-Age=${APPEARANCE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
};
